import { motion } from "framer-motion";
import { Link } from "wouter";
import {
  Sparkles, Check, SplitSquareHorizontal, LayoutGrid, ArrowRight, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaywallModal } from "@/components/paywall-modal";
import { PostGeneratorInputPanel } from "@/components/post-generator-input-panel";
import { PostGeneratorOutput } from "@/components/post-generator-output";
import { usePostGenerator } from "@/hooks/use-post-generator";

export default function PostGenerator() {
  const {
    currentIdea, setIdea, sessionId,
    showPaywall, dismissPaywall, showHardPaywall,
    mode, setMode,
    platform, setLocalPlatform,
    tone, setTone,
    copied, setCopied,
    savedToVault,
    allPlatformResults,
    generateError, generateAllError,
    postLengthOverride, setPostLengthOverride,
    feedbackGiven, setFeedbackGiven,
    feedbackMessage,
    advancedOpen, setAdvancedOpen, advancedCount,
    postStyle, setPostStyle,
    spacingStyle, setSpacingStyle,
    hookStyle, setHookStyle,
    perspective, setPerspective,
    humorLevel, setHumorLevel,
    audience, setAudience,
    emojiUsage, setEmojiUsage,
    targetWordCount, setTargetWordCount,
    customInstruction, setCustomInstruction,
    selectedVoiceState, setSelectedVoiceState,
    audienceDescription, setAudienceDescription,
    blendStyle, setBlendStyle,
    recentTopics, clearRecentContext, clearAdvanced,
    draftContent, setDraftContent,
    editedAllResults, setEditedAllResults,
    isTrimming, trimmingPlatform,
    voiceIntentBanner, setVoiceIntentBanner,
    drafts, activeDraftIdx, setActiveDraftIdx,
    undoStack,
    multiVariant, setMultiVariant,
    result,
    coachTip, coachLoading, coachDismissed, setCoachDismissed,
    voiceDnaOpen, setVoiceDnaOpen,
    needsVoiceTraining, voiceNudgeDismissed, setVoiceNudgeDismissed,
    voiceProfile, voiceSampleCount,
    hasPreferences, voiceStates,
    platformDesc, currentSuggestions, conflicts,
    topicVoiceInput, isIdeaVague,
    handleVoiceTranscriptForTopic,
    isLoading, isSingleLoading, isAllLoading,
    charCount, wordCount, platformLimit,
    handleGenerate, handleRefine, handleUndo,
    handleCopy, handleSaveToVault,
    handleTrimToLimit, handleShare, handleTrimAllPlatform,
    handlePostFeedback, handlePostKeyDown, handleUndoPush,
    updateActiveDraftContent, commitActiveDraft,
    feedbackMutationPending, createIdeaMutationPending,
  } = usePostGenerator();

  return (
    <>
      <PaywallModal show={showPaywall} onDismiss={dismissPaywall} />
      <PaywallModal show={showHardPaywall} onDismiss={() => {}} hard />
      <div className="max-w-6xl mx-auto space-y-6">
        {needsVoiceTraining && !voiceNudgeDismissed && !result && !isSingleLoading && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/40"
            style={{
              background: "linear-gradient(135deg, hsl(188 90% 42% / 0.08), hsl(265 75% 62% / 0.05))",
              boxShadow: "0 0 18px 2px hsl(188 90% 42% / 0.12), inset 0 0 0 1px hsl(188 90% 42% / 0.1)",
            }}
            data-testid="voice-nudge-banner"
          >
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <p className="text-sm text-foreground flex-1">
              <span className="font-semibold">Your voice isn't quite unique yet</span>
              {" "}— add a sample for better results.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-primary hover:text-primary/80 font-semibold px-2"
              onClick={() => window.open("/training", "_blank")}
            >
              Train now <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <button
              onClick={() => setVoiceNudgeDismissed(true)}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label="Dismiss voice training banner"
              data-testid="voice-nudge-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-display font-bold">Post Generator</h1>
              {hasPreferences && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-semibold">
                  <Check className="w-3 h-3" /> Voice Preferences active
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1">Generate posts optimized for each platform's format and audience</p>
          </div>
          <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-xl w-fit">
            <button
              onClick={() => setMode("single")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${mode === "single" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <SplitSquareHorizontal className="w-4 h-4" /> Single
            </button>
            <button
              onClick={() => setMode("all")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${mode === "all" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">All Platforms</span>
              <span className="sm:hidden">All</span>
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[420px_1fr] gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col gap-4"
          >
            <PostGeneratorInputPanel
              mode={mode}
              platform={platform}
              setLocalPlatform={setLocalPlatform}
              currentIdea={currentIdea}
              setIdea={setIdea}
              voiceIntentBanner={voiceIntentBanner}
              setVoiceIntentBanner={setVoiceIntentBanner}
              topicVoiceInput={topicVoiceInput}
              handleVoiceTranscriptForTopic={handleVoiceTranscriptForTopic}
              isIdeaVague={isIdeaVague}
              tone={tone}
              setTone={setTone}
              postLengthOverride={postLengthOverride}
              setPostLengthOverride={setPostLengthOverride}
              voiceProfilePreferences={voiceProfile?.preferences as { postLength?: string | null } | null | undefined}
              advancedOpen={advancedOpen}
              setAdvancedOpen={setAdvancedOpen}
              advancedCount={advancedCount}
              platformDesc={platformDesc as Record<string, string>}
              currentSuggestions={currentSuggestions as object}
              conflicts={conflicts}
              voiceStates={voiceStates}
              selectedVoiceState={selectedVoiceState}
              setSelectedVoiceState={setSelectedVoiceState}
              audienceDescription={audienceDescription}
              setAudienceDescription={setAudienceDescription}
              postStyle={postStyle}
              setPostStyle={setPostStyle}
              hookStyle={hookStyle}
              setHookStyle={setHookStyle}
              spacingStyle={spacingStyle}
              setSpacingStyle={setSpacingStyle}
              perspective={perspective}
              setPerspective={setPerspective}
              humorLevel={humorLevel}
              setHumorLevel={setHumorLevel}
              audience={audience}
              setAudience={setAudience}
              emojiUsage={emojiUsage}
              setEmojiUsage={setEmojiUsage}
              targetWordCount={targetWordCount}
              setTargetWordCount={setTargetWordCount}
              customInstruction={customInstruction}
              setCustomInstruction={setCustomInstruction}
              blendStyle={blendStyle}
              setBlendStyle={setBlendStyle}
              recentTopics={recentTopics}
              clearRecentContext={clearRecentContext}
              clearAdvanced={clearAdvanced}
              multiVariant={multiVariant}
              setMultiVariant={setMultiVariant}
              isLoading={isLoading}
              handleGenerate={handleGenerate}
            />
          </motion.div>

          <PostGeneratorOutput
            mode={mode}
            platform={platform}
            platformLimit={platformLimit}
            wordCount={wordCount}
            charCount={charCount}
            sessionId={sessionId}
            result={result}
            drafts={drafts}
            isSingleLoading={isSingleLoading}
            generateError={generateError}
            generateAllError={generateAllError}
            isAllLoading={isAllLoading}
            allPlatformResults={allPlatformResults}
            draftContent={draftContent}
            setDraftContent={setDraftContent}
            editedAllResults={editedAllResults}
            setEditedAllResults={setEditedAllResults}
            activeDraftIdx={activeDraftIdx}
            setActiveDraftIdx={setActiveDraftIdx}
            updateActiveDraftContent={updateActiveDraftContent}
            commitActiveDraft={commitActiveDraft}
            currentIdea={currentIdea}
            handleGenerate={handleGenerate}
            handleRefine={handleRefine}
            handleTrimToLimit={handleTrimToLimit}
            handleTrimAllPlatform={handleTrimAllPlatform}
            handleShare={handleShare}
            handleCopy={handleCopy}
            handleSaveToVault={handleSaveToVault}
            handlePostFeedback={handlePostFeedback}
            handleUndoPush={handleUndoPush}
            handleUndo={handleUndo}
            handlePostKeyDown={handlePostKeyDown}
            undoStack={undoStack}
            savedToVault={savedToVault}
            copied={copied}
            setCopied={setCopied}
            feedbackGiven={feedbackGiven}
            feedbackMessage={feedbackMessage}
            feedbackMutationPending={feedbackMutationPending}
            coachTip={coachTip}
            coachLoading={coachLoading}
            coachDismissed={coachDismissed}
            setCoachDismissed={setCoachDismissed}
            voiceDnaOpen={voiceDnaOpen}
            setVoiceDnaOpen={setVoiceDnaOpen}
            voiceProfile={voiceProfile as { sampleCount?: number; analysis?: Record<string, unknown> | null } | null | undefined}
            voiceSampleCount={voiceSampleCount}
            voiceNudgeDismissed={voiceNudgeDismissed}
            setVoiceNudgeDismissed={setVoiceNudgeDismissed}
            isTrimming={isTrimming}
            trimmingPlatform={trimmingPlatform}
            createIdeaMutationPending={createIdeaMutationPending}
            multiVariant={multiVariant}
          />
        </div>
      </div>
    </>
  );
}
